import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { B2BLayout } from "../../components/b2b/B2BLayout";
import { ArrowRight, ArrowLeft, RotateCcw } from "lucide-react";

const QUESTIONS = [
  { key: "immobili", q: "Quanti immobili vuoi gestire?", opts: [["1", "1 immobile"], ["2-5", "Da 2 a 5"], ["6-20", "Da 6 a 20"], ["20+", "Più di 20"]] },
  { key: "tipo", q: "Che tipo di immobili sono?", opts: [["appartamenti", "Appartamenti / case vacanza"], ["ricettiva", "Struttura ricettiva con camere"], ["misto", "Misto"]] },
  { key: "metratura", q: "Qual è la metratura media?", opts: [["piccola", "Fino a 50 mq"], ["media", "50-100 mq"], ["grande", "Oltre 100 mq"]] },
  { key: "split", q: "Quanti split per unità in media?", opts: [["1", "1 split"], ["2", "2 split"], ["3+", "3 o più"]] },
  { key: "frequenza", q: "Con quale frequenza vuoi i controlli?", opts: [["annuale", "Una volta l'anno"], ["semestrale", "Ogni 6 mesi"], ["trimestrale", "Ogni 3 mesi"]] },
  { key: "priorita", q: "Che priorità vuoi sugli extra?", opts: [["no", "Nessuna priorità particolare"], ["24h", "Prima uscita entro 24 ore"], ["8h", "Prima uscita entro 8 ore"]] },
];

function suggest(a) {
  let plan = "basic";
  if (a.frequenza === "semestrale" || a.priorita === "24h") plan = "pro";
  if (a.frequenza === "trimestrale" || a.priorita === "8h") plan = "premium";
  const custom = a.immobili === "20+" || a.split === "3+";
  const names = { basic: "Basic", pro: "Pro", premium: "Premium" };
  const why = {
    basic: "controlli annuali e un primo ordine nella manutenzione dei tuoi alloggi.",
    pro: "controlli semestrali e una priorità di intervento definita sugli extra.",
    premium: "controlli trimestrali e la massima priorità sulla prima uscita.",
  };
  return { plan, name: names[plan], why: why[plan], custom };
}

export default function B2BSelector() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [a, setA] = useState({});
  const result = step >= QUESTIONS.length ? suggest(a) : null;

  const pick = (key, val) => { setA({ ...a, [key]: val }); setStep(step + 1); };

  return (
    <B2BLayout>
      <section className="mx-auto max-w-2xl px-5 py-12">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl">Aiutami a scegliere il piano</h1>
        <p className="mt-2 text-sm text-[#0B1F3A]/60">Nessun importo: ti indichiamo solo il piano più adatto. La copertura viene confermata nella proposta.</p>

        {!result && (
          <div className="mt-8 rounded-3xl border border-[#0B1F3A]/10 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-[#0E7C7B]">Domanda {step + 1} di {QUESTIONS.length}</span>
              {step > 0 && <button data-testid="selector-back" onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-xs font-semibold text-[#0B1F3A]/50"><ArrowLeft className="h-4 w-4" />Indietro</button>}
            </div>
            <div className="mb-5 h-1.5 w-full overflow-hidden rounded-full bg-[#0B1F3A]/10"><div className="h-full rounded-full bg-[#0E7C7B] transition-all" style={{ width: `${(step / QUESTIONS.length) * 100}%` }} /></div>
            <h2 className="font-display text-xl font-bold text-[#0B1F3A]">{QUESTIONS[step].q}</h2>
            <div className="mt-4 space-y-2">
              {QUESTIONS[step].opts.map(([v, l]) => (
                <button key={v} data-testid={`selector-opt-${v}`} onClick={() => pick(QUESTIONS[step].key, v)}
                  className="flex w-full items-center justify-between rounded-2xl border border-[#0B1F3A]/15 bg-white px-5 py-4 text-left text-sm font-semibold text-[#0B1F3A] transition hover:border-[#0E7C7B] hover:bg-[#0E7C7B]/5">
                  {l}<ArrowRight className="h-4 w-4 text-[#0E7C7B]" />
                </button>
              ))}
            </div>
          </div>
        )}

        {result && (
          <div data-testid="selector-result" className="mt-8 rounded-3xl border border-[#0E7C7B]/30 bg-white p-8 text-center shadow-xl">
            <p className="text-sm font-semibold text-[#0B1F3A]/60">Il piano suggerito per te è</p>
            <p data-testid="selector-plan" className="mt-1 font-display text-4xl font-extrabold text-[#0E7C7B]">{result.name}</p>
            <p className="mx-auto mt-3 max-w-md text-sm text-[#0B1F3A]/70">Te lo consigliamo perché cerchi {result.why}</p>
            {result.custom && <p className="mx-auto mt-4 max-w-md rounded-2xl bg-[#F97316]/10 p-4 text-sm text-[#0B1F3A]/80">Il tuo caso supera i limiti standard: serve una valutazione personalizzata. Prepariamo una proposta dedicata dopo aver verificato gli immobili.</p>}
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button data-testid="selector-to-form" onClick={() => nav("/contatti", { state: { plan: result.plan, answers: a } })} className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-6 py-3.5 font-bold text-white shadow-lg transition hover:brightness-110">Richiedi la proposta per questo piano <ArrowRight className="h-5 w-5" /></button>
              <button onClick={() => { setA({}); setStep(0); }} className="inline-flex items-center gap-2 rounded-full border border-[#0B1F3A]/15 px-6 py-3.5 font-bold text-[#0B1F3A]"><RotateCcw className="h-4 w-4" />Ricomincia</button>
            </div>
          </div>
        )}
      </section>
    </B2BLayout>
  );
}
